import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  Typography
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axiosInstance from 'src/axios';
import { AppContext } from 'src/store/ContextStore';
import { useMemberRowSync } from 'src/components/pedigree-chart/pedigree-tool/memberRowSyncContext';
import { syncMemberGenotypesRowsAfter } from 'src/components/pedigree-chart/pedigree-sidebar/sections/genotypes-menu/memberGenotypesRow';
import { memberIdentityOf } from 'src/components/pedigree-chart/pedigree-sidebar/memberIdentity';
import { calculateAgeOnDate } from 'src/util/util';
import {
  AddressForm,
  AppointmentForm,
  ClinicalNotesForm,
  ConsentForm,
  ContactForm,
  ReferralForm,
  SampleForm,
  TestForm,
  TreatmentForm,
  TissuePathologyForm
} from 'src/components/pedigree-chart/pedigree-sidebar/sections/clinical-notes-section/forms/FormsExport';

// Each type's age field and the date it is the person's age on. Every
// replayed record carries its OWN person's age, worked out from their date of
// birth: the captured payload carries the FIRST person's, which the form
// worked out, and replayed as it was it gave everyone else that age too. An
// Address has no age.
const AGE_ON_DATE_FIELDS = {
  Test: { ageField: 'ageWhenTested', dateField: 'dateRequested' },
  Appointment: { ageField: 'ageAtRecord', dateField: 'appointmentDate' },
  'Clinical Note': { ageField: 'ageAtRecord', dateField: 'clinicalNoteDate' },
  Consent: { ageField: 'ageAtRecord', dateField: 'consentDate' },
  Contact: { ageField: 'ageAtRecord', dateField: 'contactDate' },
  Referral: { ageField: 'ageAtRecord', dateField: 'referralDate' },
  Sample: { ageField: 'ageAtRecord', dateField: 'dateTaken' },
  Treatment: { ageField: 'ageAtRecord', dateField: 'treatmentDate' },
  'Tissue Pathology': { ageField: 'ageAtRecord', dateField: 'pathReportDate' }
};

const CLINICAL_NOTE_TYPES = [
  'Address',
  'Appointment',
  'Clinical Note',
  'Consent',
  'Contact',
  'Referral',
  'Sample',
  'Test',
  'Treatment',
  'Tissue Pathology'
];

export default function BulkClinicalSection({ memberRecords, familyId }) {
  const [expanded, setExpanded] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [selectedPids, setSelectedPids] = useState(
    () => new Set(memberRecords.map((m) => m.pid))
  );
  const [staffMembers, setStaffMembers] = useState([]);

  const [formOpen, setFormOpen] = useState(false);
  const [firstMember, setFirstMember] = useState(null);
  const [replaying, setReplaying] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState({
    edit: false,
    status: false
  });
  const [lastCreated, setLastCreated] = useState(null);

  // The chart's member rows (memberRowSyncContext.js), and the family root the
  // Genotypes endpoints are addressed by. Read through a ref inside the stable
  // handleClose below.
  const rowSync = useMemberRowSync();
  const { content } = useContext(AppContext);
  const selectedUser = content?.pedigreeSelectedUsers?.selectedUser;
  const genotypeSyncRef = useRef({ rowSync, selectedUser });
  genotypeSyncRef.current = { rowSync, selectedUser };
  // The rows as they are now, for each replayed person's own date of birth.
  const memberRecordsRef = useRef(memberRecords);
  memberRecordsRef.current = memberRecords;

  // Refs that stay fresh inside stable callbacks
  const remainingMembersRef = useRef([]); // all selected members except first
  const capturedPayloadRef = useRef(null); // { url, data } from intercepted PUT
  const interceptorIdRef = useRef(null);
  const interceptActiveRef = useRef(false);
  const selectedTypeRef = useRef('');
  const totalRef = useRef(0);

  const staffMemberOptions = useMemo(
    () =>
      staffMembers.map((d) => ({
        label: `${d.firstName} ${d.lastName}`,
        value: d.userId
      })),
    [staffMembers]
  );

  useEffect(() => {
    if (expanded && staffMembers.length === 0) {
      axiosInstance.get('/clinical-notes/staff-members').then((res) => {
        setStaffMembers(
          res.data.map((m) => ({
            firstName: m.firstName,
            lastName: m.lastName,
            userId: m._id
          }))
        );
      });
    }
  }, [expanded]);

  useEffect(() => {
    setSelectedPids(new Set(memberRecords.map((m) => m.pid)));
  }, [memberRecords]);

  const togglePid = (pid) => {
    setSelectedPids((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  // Stable handleClose — on save: deactivates interceptor, replays for remaining members
  const handleClose = useCallback(async (event) => {
    if (event?.exit) {
      // Deactivate the interceptor
      interceptActiveRef.current = false;
      if (interceptorIdRef.current !== null) {
        axiosInstance.interceptors.request.eject(interceptorIdRef.current);
        interceptorIdRef.current = null;
      }

      const captured = capturedPayloadRef.current;
      const remaining = remainingMembersRef.current;
      const type = selectedTypeRef.current;
      const total = totalRef.current;

      setFormOpen(false);
      setFirstMember(null);
      capturedPayloadRef.current = null;
      remainingMembersRef.current = [];
      setUnsavedChanges({ edit: false, status: false });

      if (captured && remaining.length > 0) {
        setReplaying(true);
        let saved = 1; // first member already saved by the form
        // Each record's age is its own person's (AGE_ON_DATE_FIELDS), from
        // their own date of birth and the record's date, read as the form
        // reads it (calculateAgeOnDate). With no readable date of birth, or a
        // date before the birth, it is sent blank. PUT /test and PUT
        // /appointment derive a blank age from the date of birth the server
        // holds, as for any record saved without one; the other types have no
        // server derivation, so theirs stays blank.
        const ageFields = AGE_ON_DATE_FIELDS[type];
        const ageFor = (member, body) => {
          const row = memberRecordsRef.current.find(
            (m) => memberIdentityOf(m) === member.identity
          );
          return calculateAgeOnDate(row?.dob, body[ageFields.dateField]);
        };
        // Resolves with the people the server copied Test variants for.
        const replayAll = async () => {
          const copied = [];
          for (const member of remaining) {
            try {
              // Strip any record-specific id so a new record is created
              // eslint-disable-next-line no-unused-vars
              const { _id, __v, ...body } = captured.data;
              const res = await axiosInstance.put(captured.url, {
                ...body,
                familyMemberId: member.familyMemberId,
                ...(ageFields && {
                  [ageFields.ageField]: ageFor(member, body)
                })
              });
              saved++;
              const stored = res?.data?.genotypeCopy?.genotypes;
              if (Array.isArray(stored) && stored.length > 0) {
                copied.push({
                  familyMemberId: member.familyMemberId,
                  isProband: member.isProband,
                  stored
                });
              }
            } catch {
              // continue with remaining members
            }
          }
          return copied;
        };

        if (type === 'Test') {
          // A saved Test's variants are copied into the person's genotypes BY
          // THE SERVER, before PUT /clinical-notes/test answers. The form
          // pushes and reads back its own person, the first; nobody read the
          // rest back, and the pedigree autosave, which writes every
          // non-proband row whole from memberRecords, deleted each of their
          // copies at the next unrelated save. So the replays run with the
          // autosave held, and every person the server copied for is read
          // back onto their row before it is released: one hold for the batch
          // (memberGenotypesRow.js).
          const { rowSync: sync, selectedUser: root } = genotypeSyncRef.current;
          await syncMemberGenotypesRowsAfter({
            rowSync: sync,
            user_id: root?.user_id || root?._id,
            write: replayAll,
            requestCount: remaining.length,
            reason: 'bulk-test-variant-copy'
          }).catch((err) => {
            console.error('Failed to sync copied test variants:', err);
          });
        } else {
          await replayAll();
        }
        setReplaying(false);
        setLastCreated({ type, saved, total });
      } else {
        setLastCreated({ type, saved: 1, total });
      }
    } else {
      // Cancelled — clean up interceptor
      interceptActiveRef.current = false;
      if (interceptorIdRef.current !== null) {
        axiosInstance.interceptors.request.eject(interceptorIdRef.current);
        interceptorIdRef.current = null;
      }
      capturedPayloadRef.current = null;
      remainingMembersRef.current = [];
      setFormOpen(false);
      setFirstMember(null);
      setUnsavedChanges({ edit: false, status: false });
    }
  }, []);

  const handleCreate = () => {
    const members = memberRecords
      .filter((m) => selectedPids.has(m.pid))
      .map((m) => ({
        pid: m.pid,
        familyMemberId: m.proBandId ?? m.familyMemberId,
        isProband: !!m.proBandId,
        identity: memberIdentityOf(m)
      }));
    if (members.length === 0) return;

    selectedTypeRef.current = selectedType;
    totalRef.current = members.length;
    remainingMembersRef.current = members.slice(1);
    capturedPayloadRef.current = null;
    interceptActiveRef.current = true;

    // Intercept the first PUT to a clinical-notes endpoint to capture payload
    interceptorIdRef.current = axiosInstance.interceptors.request.use(
      (config) => {
        if (
          interceptActiveRef.current &&
          !capturedPayloadRef.current &&
          config.method === 'put' &&
          config.url?.includes('/clinical-notes/')
        ) {
          capturedPayloadRef.current = {
            url: config.url,
            data:
              typeof config.data === 'string'
                ? JSON.parse(config.data)
                : config.data
          };
        }
        return config;
      }
    );

    setLastCreated(null);
    setFirstMember(members[0]);
    setFormOpen(true);
    setUnsavedChanges({ edit: false, status: false });
  };

  const formProps = firstMember
    ? {
        handleClose,
        familyMemberId: firstMember.familyMemberId,
        // As the sidebar passes it (ClinicalNotes). Without it the Test form
        // pushed the proband's variants to a member that does not exist: the
        // Genotypes endpoints reach the family root only with `isProband`.
        isProband: firstMember.isProband,
        familyId,
        isEditing: false,
        model: {},
        staffMembers,
        staffMemberOptions,
        isDisabled: false,
        setUnsavedChanges,
        data: memberRecords.find((m) => m.pid === firstMember.pid)
      }
    : null;

  const renderForm = () => {
    if (!formProps) return null;
    switch (selectedType) {
      case 'Address':
        return <AddressForm {...formProps} />;
      case 'Appointment':
        return <AppointmentForm {...formProps} />;
      case 'Clinical Note':
        return <ClinicalNotesForm {...formProps} />;
      case 'Consent':
        return <ConsentForm {...formProps} />;
      case 'Contact':
        return <ContactForm {...formProps} />;
      case 'Referral':
        return <ReferralForm {...formProps} />;
      case 'Sample':
        return <SampleForm {...formProps} />;
      case 'Test':
        return <TestForm {...formProps} refreshPedigreeLabels={() => {}} />;
      case 'Treatment':
        return <TreatmentForm {...formProps} />;
      case 'Tissue Pathology':
        return <TissuePathologyForm {...formProps} />;
      default:
        return null;
    }
  };

  const selectedCount = selectedPids.size;

  return (
    <>
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider'
        }}
      >
        {/* Toggle header */}
        <Box
          onClick={() => setExpanded((v) => !v)}
          sx={{
            px: 3,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            bgcolor: expanded ? 'grey.100' : 'grey.50',
            userSelect: 'none'
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: 'text.secondary',
              letterSpacing: 0.5
            }}
          >
            CREATE CLINICAL RECORD FOR FAMILY MEMBERS
          </Typography>
          {expanded ? (
            <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          ) : (
            <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          )}
        </Box>

        <Collapse in={expanded}>
          <Box
            sx={{
              px: 3,
              py: 2,
              bgcolor: 'grey.50',
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            {/* Success indicator */}
            {lastCreated && (
              <Alert
                icon={<CheckCircleOutlineIcon fontSize="small" />}
                severity="success"
                onClose={() => setLastCreated(null)}
                sx={{ mb: 1.5, py: 0.5, fontSize: 13 }}
              >
                {lastCreated.type} created for {lastCreated.saved} of{' '}
                {lastCreated.total} member
                {lastCreated.total !== 1 ? 's' : ''}
              </Alert>
            )}

            {/* Replay progress */}
            {replaying && (
              <Alert
                icon={<CircularProgress size={14} />}
                severity="info"
                sx={{ mb: 1.5, py: 0.5, fontSize: 13 }}
              >
                Creating records for remaining members…
              </Alert>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 3,
                flexWrap: 'wrap'
              }}
            >
              {/* Record type */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    display: 'block',
                    mb: 0.75,
                    letterSpacing: 0.5
                  }}
                >
                  RECORD TYPE
                </Typography>
                <Select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  input={<OutlinedInput />}
                  displayEmpty
                  size="small"
                  sx={{ width: 200, height: 32, fontSize: 13 }}
                  renderValue={(v) =>
                    v ? (
                      v
                    ) : (
                      <span style={{ color: '#999', fontSize: 13 }}>
                        Select type...
                      </span>
                    )
                  }
                >
                  {CLINICAL_NOTE_TYPES.map((t) => (
                    <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Member selector */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    display: 'block',
                    mb: 0.75,
                    letterSpacing: 0.5
                  }}
                >
                  APPLY TO
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5
                  }}
                >
                  {memberRecords.map((m, i) => {
                    const checked = selectedPids.has(m.pid);
                    return (
                      <Chip
                        key={m.pid}
                        label={`UPN ${m.upn ?? i + 1}`}
                        size="small"
                        color={checked ? 'primary' : 'default'}
                        onClick={() => togglePid(m.pid)}
                        sx={{ cursor: 'pointer', height: 24, fontSize: 12 }}
                      />
                    );
                  })}
                </Box>
              </Box>

              {/* Create button */}
              <Box sx={{ ml: 'auto', alignSelf: 'flex-end' }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={!selectedType || selectedCount === 0 || replaying}
                  onClick={handleCreate}
                >
                  Create for {selectedCount} member
                  {selectedCount !== 1 ? 's' : ''}
                </Button>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Box>
      {/* Single form dialog — saves to first member; interceptor replays for rest */}
      <Dialog
        open={formOpen}
        onClose={() => handleClose({})}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 2,
            bgcolor: 'grey.900',
            color: 'white'
          }}
        >
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                lineHeight: 1.3
              }}
            >
              Create New {selectedType}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Will be created for {totalRef.current} member
              {totalRef.current !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => handleClose({})}
            sx={{ color: 'white' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3, pt: '24px !important' }}>
          {renderForm()}
        </DialogContent>
      </Dialog>
    </>
  );
}
